import React, { useState } from 'react';
import { Scale } from 'lucide-react';
import { SEO } from '../components/SEO';

interface BodyFatState {
  gender: 'M' | 'F';
  age: string;
  height: string;
  weight: string;
  neck: string;
  waist: string;
  hip: string; // Only for women
}

function KalkulatorTjelesneMasti() {
  const [values, setValues] = useState<BodyFatState>({
    gender: 'M',
    age: '',
    height: '',
    weight: '',
    neck: '',
    waist: '',
    hip: ''
  });

  const [result, setResult] = useState<{
    bodyFatPercentage: number;
    category: string;
    fatMass: number;
    leanMass: number;
  } | null>(null);

  const calculateBodyFat = () => {
    const height = parseFloat(values.height);
    const neck = parseFloat(values.neck);
    const waist = parseFloat(values.waist);
    const hip = parseFloat(values.hip);
    const weight = parseFloat(values.weight);

    if (isNaN(height) || isNaN(neck) || isNaN(waist) || (values.gender === 'F' && isNaN(hip))) {
      return null;
    }

    let bodyFatPercentage: number;

    if (values.gender === 'M') {
      // U.S. Navy method for men
      bodyFatPercentage = 495 / (1.0324 - 0.19077 * Math.log10(waist - neck) + 0.15456 * Math.log10(height)) - 450;
    } else {
      // U.S. Navy method for women
      bodyFatPercentage = 495 / (1.29579 - 0.35004 * Math.log10(waist + hip - neck) + 0.22100 * Math.log10(height)) - 450;
    }

    // Ensure the result is within reasonable bounds
    bodyFatPercentage = Math.max(2, Math.min(bodyFatPercentage, 60));

    const fatMass = (weight * bodyFatPercentage) / 100;
    const leanMass = weight - fatMass;

    return {
      bodyFatPercentage: parseFloat(bodyFatPercentage.toFixed(1)),
      category: getCategory(bodyFatPercentage, values.gender),
      fatMass: parseFloat(fatMass.toFixed(1)),
      leanMass: parseFloat(leanMass.toFixed(1))
    };
  };

  const getCategory = (percentage: number, gender: 'M' | 'F'): string => {
    if (gender === 'M') {
      if (percentage < 6) return 'Esencijalna mast';
      if (percentage < 14) return 'Atletska građa';
      if (percentage < 18) return 'Fitness građa';
      if (percentage < 25) return 'Prosječna građa';
      return 'Prekomjerna masnoća';
    } else {
      if (percentage < 14) return 'Esencijalna mast';
      if (percentage < 21) return 'Atletska građa';
      if (percentage < 25) return 'Fitness građa';
      if (percentage < 32) return 'Prosječna građa';
      return 'Prekomjerna masnoća';
    }
  };

  const handleInputChange = (field: keyof BodyFatState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCalculate = () => {
    const results = calculateBodyFat();
    setResult(results);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Tjelesne Masti",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte postotak tjelesne masti pomoću U.S. Navy metode. Precizna procjena sastava tijela na temelju mjerenja.",
    "url": "https://online-kalkulator.com/kalkulator-tjelesne-masti",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun postotka tjelesne masti",
      "Određivanje kategorije građe",
      "Izračun masne i nemasne mase",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Tjelesne Masti | Izračun Postotka Tjelesne Masti"
        description="Izračunajte postotak tjelesne masti pomoću U.S. Navy metode. Precizna procjena sastava tijela na temelju mjerenja."
        canonicalUrl="/kalkulator-tjelesne-masti"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Scale className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Tjelesne Masti</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spol
                  </label>
                  <select
                    value={values.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value as 'M' | 'F')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="M">Muško</option>
                    <option value="F">Žensko</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dob
                  </label>
                  <input
                    type="number"
                    value={values.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visina (cm)
                  </label>
                  <input
                    type="number"
                    value={values.height}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 175"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Težina (kg)
                  </label>
                  <input
                    type="number"
                    value={values.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 70"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opseg vrata (cm)
                  </label>
                  <input
                    type="number"
                    value={values.neck}
                    onChange={(e) => handleInputChange('neck', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 35"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opseg struka (cm)
                  </label>
                  <input
                    type="number"
                    value={values.waist}
                    onChange={(e) => handleInputChange('waist', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 80"
                  />
                </div>

                {values.gender === 'F' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opseg bokova (cm)
                    </label>
                    <input
                      type="number"
                      value={values.hip}
                      onChange={(e) => handleInputChange('hip', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 90"
                    />
                  </div>
                )}
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {result && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-gray-50">
                    <p className="text-2xl font-bold text-blue-600 mb-2">
                      {result.bodyFatPercentage}%
                    </p>
                    <p className="text-lg text-gray-700">
                      {result.category}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Masno tkivo</div>
                      <div className="text-lg font-semibold">{result.fatMass} kg</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Nemasno tkivo</div>
                      <div className="text-lg font-semibold">{result.leanMass} kg</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Tjelesne Masti</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator tjelesne masti koristi U.S. Navy metodu za procjenu postotka tjelesne masti.
                  Ova metoda se temelji na mjerenjima opsega tijela i pokazala se kao pouzdana alternativa
                  skupljim metodama mjerenja sastava tijela.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Mjeriti</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Vrat: Mjerite oko najužeg dijela vrata</li>
                  <li>Struk: Mjerite oko pupka, u ravnini</li>
                  <li>Bokovi (žene): Mjerite oko najšireg dijela bokova</li>
                  <li>Sva mjerenja radite u st
ojećem položaju</li>
                  <li>Koristite mjernu vrpcu paralelnu s tlom</li>
                  <li>Ne stežite mjernu vrpcu previše</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kategorije Tjelesne Masti</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Za muškarce:</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Esencijalna mast: 2-5%</li>
                      <li>Atletska građa: 6-13%</li>
                      <li>Fitness građa: 14-17%</li>
                      <li>Prosječna građa: 18-24%</li>
                      <li>Prekomjerna masnoća: 25% i više</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Za žene:</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Esencijalna mast: 10-13%</li>
                      <li>Atletska građa: 14-20%</li>
                      <li>Fitness građa: 21-24%</li>
                      <li>Prosječna građa: 25-31%</li>
                      <li>Prekomjerna masnoća: 32% i više</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Rezultati su procjena i mogu odstupati od stvarnih vrijednosti</li>
                  <li>Za najpreciznije rezultate, mjerenja radite ujutro</li>
                  <li>Konzistentnost u mjerenju je ključna za praćenje promjena</li>
                  <li>Postotak tjelesne masti varira ovisno o dobi i spolu</li>
                  <li>Za profesionalnu procjenu, posjetite stručnjaka</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Smanjenje Tjelesne Masti</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Održavajte uravnoteženu prehranu</li>
                  <li>Kombinirajte kardio i vježbe snage</li>
                  <li>Osigurajte dovoljno proteina u prehrani</li>
                  <li>Spavajte 7-9 sati dnevno</li>
                  <li>Smanjite unos prerađene hrane</li>
                  <li>Redovito pratite napredak</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorTjelesneMasti;